import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';

gsap.registerPlugin(ScrollTrigger);

const RobotCanvas = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // ── Scene ──
        const scene = new THREE.Scene();

        // ── Camera ──
        const camera = new THREE.PerspectiveCamera(
            50,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        camera.position.set(0, 0, 13);

        // ── Renderer ──
        const renderer = new THREE.WebGLRenderer({
            alpha: true,
            antialias: true,
            premultipliedAlpha: false,
        });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        container.appendChild(renderer.domElement);

        // ── Lighting ──
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
        directionalLight.position.set(5, 10, 7);
        scene.add(directionalLight);

        // Subtle fill light from the other side
        const fillLight = new THREE.DirectionalLight(0x8888ff, 0.4);
        fillLight.position.set(-5, 3, -5);
        scene.add(fillLight);

        // ── Animation Mixer ──
        let mixer = null;
        const clock = new THREE.Clock();

        // ── Load Model ──
        const loader = new GLTFLoader();
        let model = null;

        loader.load(
            '/models/snc1.glb',
            (gltf) => {
                model = gltf.scene;

                // Scale the model to a reasonable size
                model.scale.set(7, 7, 7);

                // Starting position (Hero section — top-right)
                model.position.set(8, -3, 0);
                model.rotation.set(0, -0.8, 0);

                // Enable shadows on all meshes
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                    }
                });

                scene.add(model);

                // ── Play embedded animations ──
                if (gltf.animations && gltf.animations.length > 0) {
                    mixer = new THREE.AnimationMixer(model);
                    gltf.animations.forEach((clip) => {
                        const action = mixer.clipAction(clip);
                        action.play();
                    });
                }

                // ── GSAP Scroll Animation ──
                setupScrollAnimation(model);
            },
            undefined,
            (error) => {
                console.error('Error loading robot model:', error);
            }
        );

        // ── Scroll-driven keyframe animation ──
        function setupScrollAnimation(robotModel) {
            let mm = gsap.matchMedia();

            // DESKTOP (Large screens where layout is side-by-side)
            mm.add("(min-width: 1024px)", () => {
                // Initial state for desktop (far right)
                robotModel.scale.set(7, 7, 7);
                robotModel.position.set(8, -3, 0);
                robotModel.rotation.set(0, -0.8, 0);

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: 'main',
                        start: 'top top',
                        end: 'bottom bottom',
                        scrub: 1.5,
                    },
                });

                // T0: Explicitly set Hero starting state
                tl.set(robotModel.position, { x: 8, y: -3, z: 0 }, 0);
                tl.set(robotModel.rotation, { x: 0, y: -0.8, z: 0 }, 0);

                // T0 -> T1: Hero to Highlights (Far Left)
                tl.to(robotModel.position, { x: -9, y: -2, z: 1, duration: 1, ease: 'power1.inOut' }, 0);
                tl.to(robotModel.rotation, { x: 0.2, y: 1.3, z: -0.1, duration: 1, ease: 'power1.inOut' }, 0);

                // T1-2: Highlights HOLD
                tl.to(robotModel.position, { x: -9, y: -2, z: 1, duration: 1, ease: 'none' }, 1);
                tl.to(robotModel.rotation, { x: 0.2, y: 1.3, z: -0.1, duration: 1, ease: 'none' }, 1);

                // T2-T3: Model Section (Quick Disappear)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 0.5, ease: 'power2.inOut' }, 2);
                tl.to(robotModel.rotation, { x: -0.5, y: 0, z: 0, duration: 0.5, ease: 'power2.inOut' }, 2);

                // T3-T4: BentoGrid Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 3);

                // T4-T5: Meet Our Team Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 4);

                // T5-T6: Contact Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 5);

                // T6-7: Footer Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 6);
            });

            // TABLET (Mid-sized screens where layout is vertically stacked)
            mm.add("(min-width: 768px) and (max-width: 1023px)", () => {
                // Robot centered between text and globe, pushed down due to taller Hero
                robotModel.scale.set(4.5, 4.5, 4.5);
                robotModel.position.set(0, -4, 0);
                robotModel.rotation.set(0, -0.8, 0);

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: 'main',
                        start: 'top top',
                        end: 'bottom bottom',
                        scrub: 1.5,
                    },
                });

                // T0: Explicitly set Tablet Hero starting state
                tl.set(robotModel.position, { x: 0, y: -4, z: 0 }, 0);
                tl.set(robotModel.rotation, { x: 0, y: -0.8, z: 0 }, 0);

                // T0 -> T1: Hero to Highlights (Move Left)
                tl.to(robotModel.position, { x: -4, y: -2, z: 1, duration: 1, ease: 'power1.inOut' }, 0);
                tl.to(robotModel.rotation, { x: 0.2, y: 1.3, z: -0.1, duration: 1, ease: 'power1.inOut' }, 0);

                // T1-1.5: Highlights HOLD
                tl.to(robotModel.position, { x: -4, y: -2, z: 1, duration: 0.5, ease: 'none' }, 1);
                tl.to(robotModel.rotation, { x: 0.2, y: 1.3, z: -0.1, duration: 0.5, ease: 'none' }, 1);

                // T1.5-2: Quick Disappear (Before Model section starts)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 0.5, ease: 'power2.inOut' }, 1.5);
                tl.to(robotModel.rotation, { x: -0.5, y: 0, z: 0, duration: 0.5, ease: 'power2.inOut' }, 1.5);

                // Keep hidden for remaining sections...
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 2);
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 3);
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 4);
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 5);
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 6);
            });

            // MOBILE (Smaller screens)
            mm.add("(max-width: 767px)", () => {
                // Initial state for mobile
                robotModel.scale.set(4, 4, 4);
                robotModel.position.set(1.2, -1.5, 0);
                robotModel.rotation.set(0, -0.8, 0);

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: 'main',
                        start: 'top top',
                        end: 'bottom bottom',
                        scrub: 1.5,
                    },
                });

                // T0: Explicitly set mobile Hero starting state
                tl.set(robotModel.position, { x: 1.2, y: -1.5, z: 0 }, 0);
                tl.set(robotModel.rotation, { x: 0, y: -0.8, z: 0 }, 0);

                // T0 -> T1: Hero to Highlights (Left)
                tl.to(robotModel.position, { x: -2.8, y: -1.5, z: 1, duration: 1, ease: 'power1.inOut' }, 0);
                tl.to(robotModel.rotation, { x: 0.2, y: 1.3, z: -0.1, duration: 1, ease: 'power1.inOut' }, 0);

                // T1-1.5: Highlights HOLD
                tl.to(robotModel.position, { x: -2.8, y: -1.5, z: 1, duration: 0.5, ease: 'none' }, 1);
                tl.to(robotModel.rotation, { x: 0.2, y: 1.3, z: -0.1, duration: 0.5, ease: 'none' }, 1);

                // T1.5-2: Quick Disappear (Before Model section starts)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 0.5, ease: 'power2.inOut' }, 1.5);
                tl.to(robotModel.rotation, { x: -0.5, y: 0, z: 0, duration: 0.5, ease: 'power2.inOut' }, 1.5);

                // T3-T4: BentoGrid Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 3);

                // T4-T5: Meet Our Team Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 4);

                // T5-T6: Contact Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 5);

                // T6-7: Footer Section (Hidden Hold)
                tl.to(robotModel.position, { x: 0, y: 15, z: -15, duration: 1, ease: 'none' }, 6);
            });
        }

        // ── Render Loop ──
        let animFrameId;
        function animate() {
            animFrameId = requestAnimationFrame(animate);
            const delta = clock.getDelta();

            if (mixer) {
                mixer.update(delta);
            }

            renderer.render(scene, camera);
        }
        animate();

        // ── Resize Handler ──
        function handleResize() {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        }
        window.addEventListener('resize', handleResize);

        // ── Cleanup ──
        return () => {
            cancelAnimationFrame(animFrameId);
            window.removeEventListener('resize', handleResize);

            // Kill all ScrollTrigger instances for this timeline
            ScrollTrigger.getAll().forEach((st) => st.kill());

            renderer.dispose();
            if (container.contains(renderer.domElement)) {
                container.removeChild(renderer.domElement);
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className="robot-canvas"
        />
    );
};

export default RobotCanvas;
